import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { userIdToken } from '@/services/auth';
import { $log } from '@/main';
import { showErrorToastMessage } from '@/utils/toastMessage';
import {
  CCP_API_GATEWAY,
  CCP_AUDIO_CHAT_API_GATEWAY,
  CONTACT_HISTORY_API_GATEWAY,
  KPI_API_GATEWAY,
  CALLER_COUNT_API_GATEWAY,
  DYNAMIC_BUTTONS_API_GATEWAY,
  STOP_TASK_API_GATEWAY,
  EMAIL_INFO_API_GATEWAY,
  SEND_EMAIL_API_GATEWAY,
  CONTACTS_API_GATEWAY,
  SUPPORT_API_GATEWAY
} from '@/constants/aws';

const ccpClient = axios.create({
  baseURL: CCP_API_GATEWAY,
  timeout: 30000
});

const ccpAudioChatClient = axios.create({
  baseURL: CCP_AUDIO_CHAT_API_GATEWAY
});

const contactHistoryClient = axios.create({
  baseURL: CONTACT_HISTORY_API_GATEWAY
});

const kpiClient = axios.create({
  baseURL: KPI_API_GATEWAY
});

const callerCountClient = axios.create({
  baseURL: CALLER_COUNT_API_GATEWAY
});

const dynamicButtonsClient = axios.create({
  baseURL: DYNAMIC_BUTTONS_API_GATEWAY
});

const stopTaskClient = axios.create({
  baseURL: STOP_TASK_API_GATEWAY
});

const emailnfo = axios.create({
  baseURL: EMAIL_INFO_API_GATEWAY
});

const sendEmail = axios.create({
  baseURL: SEND_EMAIL_API_GATEWAY
});

const contactCounters = axios.create(
  { baseURL: CONTACTS_API_GATEWAY }
);

const supportClient = axios.create({
  baseURL: SUPPORT_API_GATEWAY
});

const createInterceptors = (client: AxiosInstance) => {
  /**
   * Create request interceptors
   */
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      config.headers.Authorization = `Bearer ${userIdToken.value}`;
      return config;
    },
    (error) => {
      const { config, message } = error;
      const { url, params } = config;

      $log.error(`URL ${url}`, params, message);

      showErrorToastMessage(`ERROR ${url}`);
      return Promise.reject(error);
    }
  );

  /**
   * Create response interceptors
   */
  client.interceptors.response.use(
    (response) => {
      const { config, data } = response;
      const { url, params } = config;

      const bodyParams = config?.data ? JSON.parse(config?.data) : {};

      $log.info(`URL ${url}`, params, bodyParams, data);
      return response;
    },
    (error) => {
      const { config, message } = error;
      const { url, params } = config;

      const bodyParams = config?.data ? JSON.parse(config?.data) : {};

      $log.error(`URL ${url}`, params, bodyParams, message);

      return Promise.reject(error);
    }
  );
};

createInterceptors(ccpClient);
createInterceptors(ccpAudioChatClient);
createInterceptors(contactHistoryClient);
createInterceptors(kpiClient);
createInterceptors(callerCountClient);
createInterceptors(dynamicButtonsClient);
createInterceptors(stopTaskClient);
createInterceptors(emailnfo);
createInterceptors(sendEmail);
createInterceptors(contactCounters);
createInterceptors(supportClient);

export {
  ccpClient,
  ccpAudioChatClient,
  contactHistoryClient,
  kpiClient,
  callerCountClient,
  dynamicButtonsClient,
  stopTaskClient,
  emailnfo,
  sendEmail,
  contactCounters,
  supportClient
};